import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, Loader2, Users } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTranslation } from "@/i18n/translations";
import { useAuth } from "@/contexts/AuthContext";
import {
  useFolderUsersQuery,
  useAssignUserToFolderMutation,
  useUpdateFolderUserRoleMutation,
  useRemoveFolderUserMutation,
} from "@/hooks/queries/useFolderUsersQuery";
import { useProjectUsersQuery, useRolesQuery } from "@/hooks/queries/useProjectUsersQuery";
import { RoleType } from "@/types/project";
import type { FolderUser } from "@/types/folder";
import type { ProjectUser } from "@/types/project";

interface ManageFolderUsersDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  folderId: number;
  folderName: string;
  projectId: number;
}

export function ManageFolderUsersDialog({
  open,
  onOpenChange,
  folderId,
  folderName,
  projectId,
}: ManageFolderUsersDialogProps) {
  const { language } = useLanguage();
  const { t } = useTranslation(language);
  const { user, isLoading: authLoading } = useAuth();

  const currentUserId = Number(user?.id ?? 0);
  const { data: projectUsers = [], isLoading: projectUsersLoading } = useProjectUsersQuery(projectId);
  const { data: folderUsers = [], isLoading } = useFolderUsersQuery(folderId, open);
  const { data: roles = [] } = useRolesQuery();

  const assignMutation = useAssignUserToFolderMutation();
  const updateRoleMutation = useUpdateFolderUserRoleMutation();
  const removeMutation = useRemoveFolderUserMutation();

  const [showAddPanel, setShowAddPanel] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string>("");
  const [userToRemove, setUserToRemove] = useState<FolderUser | null>(null);

  // Determine current user's project role
  const currentProjectMembership = projectUsers.find((pu) => pu.userId === currentUserId);
  const isProjectOwner = currentProjectMembership?.roleType === RoleType.Owner;
  const isProjectEditor = currentProjectMembership?.roleType === RoleType.Editor;

  // Determine current user's folder role
  const currentFolderMembership = folderUsers.find((fu) => fu.userId === currentUserId);
  const isFolderOwner = currentFolderMembership?.roleType === RoleType.Owner;

  // Who can see/use the dialog: folder owner, project owner
  const canViewDialog = isProjectOwner || isFolderOwner;
  const permissionsLoading = authLoading || projectUsersLoading;

  // Who can add users: project owner or project editor
  const canAddUsers = isProjectOwner || isProjectEditor;

  // Who can change roles / remove users: folder owner, project owner
  const canManageUsers = isProjectOwner || isFolderOwner;

  // Build list of project users that are not already in the folder and are not the current user
  const folderUserIds = new Set(folderUsers.map((fu) => fu.userId));
  const availableToAdd: ProjectUser[] = projectUsers.filter(
    (pu) => pu.userId !== currentUserId && !folderUserIds.has(pu.userId)
  );

  const getRoleLabel = (roleType: number) => {
    switch (roleType) {
      case RoleType.Owner: return t("roleOwner");
      case RoleType.Editor: return t("roleEditor");
      case RoleType.Viewer: return t("projectViewer");
      default: return String(roleType);
    }
  };

  const getRoleBadgeVariant = (roleType: number): "default" | "secondary" | "outline" => {
    switch (roleType) {
      case RoleType.Owner: return "default";
      case RoleType.Editor: return "secondary";
      default: return "outline";
    }
  };

  const handleAddUser = () => {
    if (!selectedUserId) return;
    assignMutation.mutate(
      { folderId, userId: Number(selectedUserId) },
      {
        onSuccess: () => {
          setSelectedUserId("");
          setShowAddPanel(false);
        },
      }
    );
  };

  const handleRoleChange = (userId: number, newRoleId: string) => {
    updateRoleMutation.mutate({ folderId, userId, roleId: Number(newRoleId) });
  };

  const handleConfirmRemove = () => {
    if (!userToRemove) return;
    removeMutation.mutate(
      { folderId, userId: userToRemove.userId },
      { onSuccess: () => setUserToRemove(null) }
    );
  };

  if (!canViewDialog && !isLoading && !permissionsLoading) {
    return null;
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="flex max-h-[85vh] flex-col sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              {t("manageFolderUsers")}: {folderName}
            </DialogTitle>
          </DialogHeader>

          <div className="flex min-h-0 flex-1 flex-col space-y-4">
            {/* Add user panel toggle */}
            {(canAddUsers || permissionsLoading) && (
              <div className="flex justify-end">
                <Button
                  size="sm"
                  variant={showAddPanel ? "outline" : "default"}
                  onClick={() => {
                    setShowAddPanel((v) => !v);
                    setSelectedUserId("");
                  }}
                  disabled={permissionsLoading}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  {t("addFolderMember")}
                </Button>
              </div>
            )}

            {/* Add user inline panel */}
            {showAddPanel && canAddUsers && (
              <div className="flex gap-2 items-center border rounded-md p-3 bg-muted/30">
                <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                  <SelectTrigger className="flex-1 h-9">
                    <SelectValue placeholder={t("selectProjectUser")} />
                  </SelectTrigger>
                  <SelectContent>
                    {availableToAdd.length === 0 ? (
                      <div className="py-2 px-3 text-sm text-muted-foreground">
                        {t("noUsersAvailable")}
                      </div>
                    ) : (
                      availableToAdd.map((pu) => (
                        <SelectItem key={pu.userId} value={String(pu.userId)}>
                          {pu.userNickName
                            ? `${pu.userNickName} (${pu.userEmail})`
                            : pu.userEmail}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                <Button
                  size="sm"
                  onClick={handleAddUser}
                  disabled={!selectedUserId || assignMutation.isPending}
                >
                  {assignMutation.isPending && (
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  )}
                  {t("addTeamMember")}
                </Button>
              </div>
            )}

            {/* User table */}
            {isLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : folderUsers.length === 0 ? (
              <div className="text-center py-8 text-sm text-muted-foreground">
                {t("noFolderMembers")}
              </div>
            ) : (
              <ScrollArea className="h-72 min-h-0 flex-1 rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("name")}</TableHead>
                      <TableHead className="w-36">{t("permission")}</TableHead>
                      {(canManageUsers || permissionsLoading) && <TableHead className="w-12" />}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {folderUsers.map((fu) => (
                      <TableRow key={fu.id}>
                        <TableCell>
                          <div className="font-medium text-sm">
                            {fu.userNickName ?? (
                              <span className="text-muted-foreground italic">—</span>
                            )}
                          </div>
                          <div className="text-xs text-muted-foreground">{fu.userEmail}</div>
                        </TableCell>
                        <TableCell>
                          {canManageUsers && roles.length > 0 ? (
                            <Select
                              value={String(fu.roleId)}
                              onValueChange={(val) => handleRoleChange(fu.userId, val)}
                              disabled={updateRoleMutation.isPending}
                            >
                              <SelectTrigger className="h-8 w-32">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {roles.map((role) => (
                                  <SelectItem key={role.id} value={String(role.id)}>
                                    {getRoleLabel(role.roleType)}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <Badge variant={getRoleBadgeVariant(fu.roleType)}>
                              {getRoleLabel(fu.roleType)}
                            </Badge>
                          )}
                        </TableCell>
                        {(canManageUsers || permissionsLoading) && (
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-muted-foreground hover:text-destructive"
                              onClick={() => setUserToRemove(fu)}
                              title={t("removeMember")}
                              disabled={permissionsLoading}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Remove confirmation */}
      <AlertDialog
        open={!!userToRemove}
        onOpenChange={(open) => { if (!open) setUserToRemove(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("confirmRemoveFolderUser")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("confirmRemoveFolderUserDesc")}
              {userToRemove && (
                <span className="block mt-1 font-medium text-foreground">
                  {userToRemove.userEmail}
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleConfirmRemove}
              disabled={removeMutation.isPending}
            >
              {removeMutation.isPending && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              {t("removeMember")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export default ManageFolderUsersDialog;
